import { createContext, useContext, useState, useEffect } from 'react';
import fr from '../locales/fr.json';
import en from '../locales/en.json';

const translations = { fr, en };

const LanguageContext = createContext(null);

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export const useTranslation = () => {
  const { language } = useLanguage();
  
  const t = (key, params = {}) => {
    const keys = key.split('.');
    let value = translations[language];
    
    for (const k of keys) {
      if (value && typeof value === 'object') {
        value = value[k];
      } else {
        return key; // Return key if translation not found
      }
    }
    
    if (typeof value === 'string' && Object.keys(params).length > 0) {
      // Replace {param} with actual values
      return value.replace(/{(\w+)}/g, (match, paramName) => {
        return params[paramName] !== undefined ? params[paramName] : match;
      });
    }
    
    return value || key;
  };
  
  return { t, language };
};

export function LanguageProvider({ children }) {
  const [language, setLanguage] = useState(() => {
    // Choix enregistré, sinon français pour un navigateur francophone, anglais ailleurs
    const saved = localStorage.getItem('language');
    return saved || ((navigator.language || 'fr').toLowerCase().startsWith('fr') ? 'fr' : 'en');
  });

  useEffect(() => {
    localStorage.setItem('language', language);
    // Update document lang attribute
    document.documentElement.lang = language;
  }, [language]);

  const toggleLanguage = () => {
    setLanguage(prev => prev === 'fr' ? 'en' : 'fr');
  };

  const value = {
    language,
    setLanguage,
    toggleLanguage,
    isFrench: language === 'fr',
    isEnglish: language === 'en'
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
}

export default LanguageContext;